import {
  buildDealPayrollGroupSettingsRequest, DEFAULT_PLANNED_PAYMENT_GRACE_DAYS,
  DealPayrollGrade, DealPayrollGroupSettings
} from 'app/models/deal-payroll.model';
import {
  cloneDealPayrollGroupSettings, isDealPayrollGroupSettingsDraftPristine,
  restoreDealPayrollGroupSettingsDraft
} from 'app/models/deal-payroll-settings.adapter';
import {dealPayrollBlockerKeys} from 'app/models/deal-payroll-presentation';
import {CurrencyCodes} from 'app/models/payroll-currency.model';
import {
  DEAL_PAYROLL_GROUP_SETTINGS_EMPTY_VALUE, DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY,
  DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY, DEAL_PAYROLL_GROUP_SETTINGS_INITIAL_SEQUENCE,
  DEAL_PAYROLL_GROUP_SETTINGS_REMOVE_POSITION_COUNT,
  DEAL_PAYROLL_GROUP_SETTINGS_SAVED_RESULT
} from './deal-payroll-group-settings.constants';
import {
  dealPayrollGroupGradeLabels, DealPayrollGroupSettingsViewModel,
  isDealPayrollGroupSettingsBusy
} from './deal-payroll-group-settings.selectors';
import {
  DealPayrollGroupSettingsFormPort, DealPayrollGroupSettingsRequestContext,
  DealPayrollGroupSettingsSaveContext
} from './deal-payroll-group-settings.types';

export class DealPayrollGroupSettingsState {
  readonly viewState: DealPayrollGroupSettingsViewModel;

  private currentGroupId: string = null;
  private requestSequence = DEAL_PAYROLL_GROUP_SETTINGS_INITIAL_SEQUENCE;
  private saveSequence = DEAL_PAYROLL_GROUP_SETTINGS_INITIAL_SEQUENCE;
  private conflictDraft: DealPayrollGroupSettings = null;
  private authoritativeSettings: DealPayrollGroupSettings = null;
  private acknowledgedSave = false;
  private form: DealPayrollGroupSettingsFormPort = null;

  constructor() {
    this.viewState = new DealPayrollGroupSettingsViewModel(
      () => !!this.conflictDraft, () => this.isCurrentDraftPristine());
  }

  get groupId(): string { return this.currentGroupId; }
  attachForm(form: DealPayrollGroupSettingsFormPort): void { this.form = form; }

  changeGroup(groupId: string): boolean {
    if (!groupId || groupId === this.currentGroupId) { return false; }
    this.currentGroupId = groupId;
    this.resetForGroupChange();
    return true;
  }

  startLoading(preserveError: boolean): DealPayrollGroupSettingsRequestContext {
    if (!this.currentGroupId) { return null; }
    const context = this.nextRequestContext();
    this.viewState.loading = true;
    this.viewState.areaCatalogRefreshing = false;
    this.viewState.successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.NONE;
    if (!preserveError) { this.viewState.errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.NONE; }
    return context;
  }

  startAreaCatalogRefresh(): DealPayrollGroupSettingsRequestContext {
    if (!this.viewState.canRetryAreaCatalog) { return null; }
    const context = this.nextRequestContext();
    this.viewState.areaCatalogRefreshing = true;
    this.clearFeedback();
    return context;
  }

  startSaving(): DealPayrollGroupSettingsSaveContext {
    if (!this.viewState.canSave) { return null; }
    const context = this.nextSaveContext();
    this.viewState.saving = true;
    this.clearFeedback();
    return context;
  }

  applyLoadedSettings(response: DealPayrollGroupSettings,
                      context: DealPayrollGroupSettingsRequestContext): void {
    if (!this.isCurrentRequest(context)) { return; }
    if (!this.matchesContext(response, context)) {
      this.rejectInvalidLoad();
      return;
    }
    this.applyAuthoritativeSettings(response);
    this.viewState.loading = false;
    this.viewState.canRestoreConflictDraft = !!this.conflictDraft;
    if (this.viewState.canRestoreConflictDraft) {
      this.viewState.errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.STALE;
    }
  }

  applyAreaCatalogSettings(response: DealPayrollGroupSettings,
                           context: DealPayrollGroupSettingsRequestContext): void {
    if (!this.isCurrentRequest(context)) { return; }
    this.viewState.areaCatalogRefreshing = false;
    if (!this.matchesContext(response, context)) {
      this.viewState.errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.LOAD;
      return;
    }
    this.applyAuthoritativeSettings(response);
    this.viewState.errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.NONE;
  }

  applySavedSettings(response: DealPayrollGroupSettings,
                     context: DealPayrollGroupSettingsSaveContext): void {
    if (!this.isCurrentSave(context)) { return; }
    this.viewState.saving = false;
    if (!this.matchesContext(response, context)) {
      this.viewState.errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.SAVE;
      this.viewState.successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.NONE;
      return;
    }
    this.finishAcknowledgedSave(response);
  }

  rejectLoad(context: DealPayrollGroupSettingsRequestContext, errorKey: string,
             blockerCodes: string[]): void {
    if (!this.isCurrentRequest(context)) { return; }
    this.viewState.loading = false;
    this.viewState.successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.NONE;
    this.viewState.errorKey = errorKey;
    this.viewState.blockerKeys = dealPayrollBlockerKeys(blockerCodes);
  }

  rejectAreaCatalog(context: DealPayrollGroupSettingsRequestContext, errorKey: string): void {
    if (!this.isCurrentRequest(context)) { return; }
    this.viewState.areaCatalogRefreshing = false;
    this.viewState.successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.NONE;
    this.viewState.errorKey = errorKey;
  }

  rejectSave(context: DealPayrollGroupSettingsSaveContext, errorKey: string,
             blockerCodes: string[]): boolean {
    if (!this.isCurrentSave(context)) { return false; }
    this.viewState.saving = false;
    this.viewState.successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.NONE;
    this.viewState.blockerKeys = dealPayrollBlockerKeys(blockerCodes);
    this.viewState.errorKey = errorKey;
    if (errorKey !== DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.STALE) { return false; }
    this.conflictDraft = context.draft;
    return true;
  }

  restoreConflict(): void {
    if (!this.canRestoreConflict()) { return; }
    this.viewState.settings = restoreDealPayrollGroupSettingsDraft(
      this.viewState.settings, this.conflictDraft);
    this.conflictDraft = null;
    this.viewState.canRestoreConflictDraft = false;
    this.viewState.blockerKeys = dealPayrollBlockerKeys(this.viewState.settings.blockers);
    this.draftChanged();
  }

  addPosition(): void {
    if (this.viewState.editingDisabled) { return; }
    this.viewState.settings.payrollPositions.push({
      ownerGroupId: this.viewState.settings.groupId,
      ownerGroupName: this.viewState.settings.groupName,
      positionId: null, displayName: DEAL_PAYROLL_GROUP_SETTINGS_EMPTY_VALUE,
      plannedHourRate: null, actualHourRate: null, active: true,
      rateCurrency: this.viewState.settings.payrollCurrency || CurrencyCodes.BYN
    });
    this.draftChanged();
  }

  discardPosition(index: number): void {
    if (this.viewState.editingDisabled) { return; }
    const position = this.viewState.settings && this.viewState.settings.payrollPositions[index];
    if (position && !position.positionId) {
      this.viewState.settings.payrollPositions.splice(
        index, DEAL_PAYROLL_GROUP_SETTINGS_REMOVE_POSITION_COUNT);
      this.draftChanged();
    }
  }

  gradeEnabledChanged(grade: DealPayrollGrade): void {
    if (this.viewState.editingDisabled) { return; }
    if (grade.inUse && !grade.enabled) { grade.enabled = true; return; }
    this.draftChanged();
  }

  draftChanged(): void {
    if (this.viewState.editingDisabled) { return; }
    this.viewState.successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.NONE;
    if (this.form) { this.form.markAsDirty(); }
  }

  modalResult(): string {
    return this.acknowledgedSave ? DEAL_PAYROLL_GROUP_SETTINGS_SAVED_RESULT : undefined;
  }

  private nextRequestContext(): DealPayrollGroupSettingsRequestContext {
    return {groupId: this.currentGroupId, sequence: ++this.requestSequence};
  }

  private nextSaveContext(): DealPayrollGroupSettingsSaveContext {
    return {draft: cloneDealPayrollGroupSettings(this.viewState.settings),
      groupId: this.currentGroupId, sequence: ++this.saveSequence,
      request: buildDealPayrollGroupSettingsRequest(this.viewState.settings)};
  }

  private matchesContext(response: DealPayrollGroupSettings,
                         context: DealPayrollGroupSettingsRequestContext): boolean {
    return !!response && response.groupId === context.groupId;
  }

  private isCurrentRequest(context: DealPayrollGroupSettingsRequestContext): boolean {
    return this.currentGroupId === context.groupId && this.requestSequence === context.sequence;
  }

  private isCurrentSave(context: DealPayrollGroupSettingsSaveContext): boolean {
    return this.currentGroupId === context.groupId && this.saveSequence === context.sequence;
  }

  private isCurrentDraftPristine(): boolean {
    return !this.conflictDraft && isDealPayrollGroupSettingsDraftPristine(
      this.viewState.settings, this.authoritativeSettings)
      && (!this.form || this.form.isPristine());
  }

  private canRestoreConflict(): boolean {
    return !!this.viewState.settings && !!this.conflictDraft
      && !isDealPayrollGroupSettingsBusy(this.viewState);
  }

  private finishAcknowledgedSave(response: DealPayrollGroupSettings): void {
    this.applyAuthoritativeSettings(response);
    this.viewState.errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.NONE;
    this.viewState.successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.SAVED;
    this.acknowledgedSave = true;
    this.conflictDraft = null;
    this.viewState.canRestoreConflictDraft = false;
  }

  private rejectInvalidLoad(): void {
    this.viewState.loading = false;
    this.viewState.settings = null;
    this.authoritativeSettings = null;
    this.viewState.errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.LOAD;
  }

  private applyAuthoritativeSettings(response: DealPayrollGroupSettings): void {
    this.viewState.settings = cloneDealPayrollGroupSettings(response);
    if (this.viewState.settings.plannedPaymentGraceDays == null) {
      this.viewState.settings.plannedPaymentGraceDays = DEFAULT_PLANNED_PAYMENT_GRACE_DAYS;
    }
    this.authoritativeSettings = cloneDealPayrollGroupSettings(this.viewState.settings);
    this.viewState.gradeLabels = dealPayrollGroupGradeLabels(this.viewState.settings);
    this.viewState.blockerKeys = dealPayrollBlockerKeys(response.blockers);
    if (this.form) { this.form.markAsPristine(); }
  }

  private clearFeedback(): void {
    this.viewState.errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.NONE;
    this.viewState.successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.NONE;
  }

  private resetForGroupChange(): void {
    this.requestSequence++;
    this.saveSequence++;
    this.viewState.settings = null;
    this.viewState.blockerKeys = [];
    this.viewState.gradeLabels = {};
    this.viewState.loading = false;
    this.viewState.saving = false;
    this.viewState.areaCatalogRefreshing = false;
    this.conflictDraft = null;
    this.authoritativeSettings = null;
    this.acknowledgedSave = false;
    this.viewState.canRestoreConflictDraft = false;
    this.clearFeedback();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/deal-payroll-group-settings/deal-payroll-group-settings.state.ts